import type React from 'react';
import { memo } from 'react';
import { useCanAccess } from '../../../access-control/hooks/useCanAccess';
import {
  useBlockRuntimeData,
  useRuntimeBlockKey,
} from '../../../context/BlockRuntimeStateProvider';
import { useSlotContextSnapshots } from '../../../context/SlotContextSnapshotsProvider/SlotContextSnapshotsContext';
import useSetBlockState from '../../../hooks/useSetBlockState';
import type { WithBlockStateProps, WithContextProps } from '../types';

export const withBlockState = (WrappedComponent: React.ComponentType<WithContextProps>) => {
  function WithBlockStateWrapper(props: WithBlockStateProps) {
    const { block, ...restProps } = props;
    const { id } = block;
    const runtimeBlockKey = useRuntimeBlockKey();
    const { data: blocksRuntimeState } = useBlockRuntimeData();
    const slotContextSnapshots = useSlotContextSnapshots();

    const { blockState, updateBlockState, updateBlockSessionState, blockStateId } =
      useSetBlockState({
        originalBlockId: id,
        runtimeBlockKey,
      });

    const isBlockPermissible = useCanAccess(block.permissions);
    if (!isBlockPermissible) {
      return null;
    }

    return (
      <WrappedComponent
        {...restProps}
        block={block}
        blockState={blockState}
        blockStateId={blockStateId}
        blocksRuntimeState={blocksRuntimeState}
        computedBlockState={blockState}
        slotContextSnapshots={slotContextSnapshots}
        updateBlockSessionState={updateBlockSessionState}
        updateBlockState={updateBlockState}
      />
    );
  }

  return memo(WithBlockStateWrapper);
};
